"use client";

import type { ReactNode } from "react";

type WorldLayersProps = {
  modelReady: boolean;
  charging: boolean;
  /** The Three.js volcano stage canvas is mounted here between photo and aurora. */
  children: ReactNode;
};

/** Layered atmospheric backdrop: photo, 3D stage, fallback, aurora, smoke, scrim, grain, hand orb. */
export function WorldLayers({ modelReady, charging, children }: WorldLayersProps) {
  return (
    <>
      <div className="absolute inset-0 -z-[4] overflow-hidden bg-[#07100c]">
        <div
          className="ng-photo absolute -inset-[4%] bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/images/garden-bg.png')",
            transformOrigin: "50% 62%",
            animation: "ng-breath 13s ease-in-out infinite alternate",
          }}
        />
        {children}
        <img
          src="/images/volcano-fallback.png"
          alt=""
          aria-hidden
          className="ng-fallback absolute left-1/2 top-[49%] w-[min(70vw,420px)] max-w-[92%] -translate-x-1/2 -translate-y-1/2"
          style={{
            opacity: modelReady ? 0 : 0.88,
            filter: "drop-shadow(0 0 42px rgba(226,74,43,.38))",
            transition: "opacity .6s ease",
            animation: "ng-volcano-pulse 5s ease-in-out infinite alternate",
          }}
        />
        <div
          className="ng-aurora pointer-events-none absolute inset-0 -z-[2]"
          style={{
            mixBlendMode: "screen",
            opacity: 0.74,
            background:
              "radial-gradient(70% 30% at 38% 8%,rgba(126,216,176,.34),transparent 55%),radial-gradient(50% 24% at 68% 15%,rgba(128,92,210,.24),transparent 60%)",
            filter: "blur(20px)",
            animation: "ng-aurora 16s ease-in-out infinite alternate",
          }}
        />
        <div
          className="ng-smoke pointer-events-none absolute inset-0 -z-[1]"
          style={{
            background:
              "radial-gradient(36% 28% at 49% 43%,rgba(215,242,232,.17),transparent 68%)",
            filter: "blur(18px)",
            animation: "ng-smoke 9s ease-in-out infinite alternate",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 58%,rgba(126,216,176,.08),transparent 34%),linear-gradient(to bottom,rgba(5,9,8,.34),rgba(3,5,4,.15) 44%,rgba(4,8,6,.72) 100%)",
          }}
        />
      </div>

      {/* Grain overlay */}
      <div
        className="pointer-events-none absolute inset-0 z-[6]"
        style={{
          opacity: 0.13,
          backgroundImage:
            "repeating-radial-gradient(circle at 17% 21%,rgba(255,255,255,.55) 0 1px,transparent 1px 3px)",
          mixBlendMode: "overlay",
        }}
      />

      {/* Hand orb following pointer */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-[calc(50%-31px)] top-[44%] h-[62px] w-[62px] rounded-full"
        style={{
          border: "1px solid rgba(126,216,176,.55)",
          boxShadow:
            "0 0 26px rgba(126,216,176,.45), inset 0 0 22px rgba(126,216,176,.18)",
          background:
            "radial-gradient(circle,rgba(126,216,176,.28),rgba(126,216,176,.03))",
          opacity: charging ? 1 : 0.8,
          transform: "translate3d(var(--mx,0px),var(--my,0px),0)",
          transition: "opacity .3s ease",
          animation: charging
            ? "ng-charge 1.1s ease-in-out infinite alternate"
            : undefined,
        }}
      />
    </>
  );
}
